import { fetchJSON as fetchAPI } from './client';

export type FinanceOpsDashboard = {
  tenant_id: string;
  total_settlements: number;
  pending_payout: number;
  reserve_balance: number;
  total_fees: number;
  fee_pct_of_revenue: number;
  reconciliation_rate: number;
  open_leakages: number;
  leakage_amount: number;
  financial_health_score: number;
  active_recommendations: number;
  generated_at?: string;
};

export type SettlementRecord = {
  id: string;
  settlement_id?: string;
  status: string;
  total_amount: number;
  reserve_balance: number;
  payment_ref?: string;
  bank_status?: string;
  period_start?: string;
  period_end?: string;
  expected_release?: string;
};

export type FinancialEventRecord = {
  id: string;
  settlement_id?: string;
  event_type: string;
  description?: string;
  sku?: string;
  order_id?: string;
  amount: number;
  currency?: string;
  event_date?: string;
};

export type FeeRecord = {
  id: string;
  fee_type: string;
  sku?: string;
  category?: string;
  amount: number;
  pct_of_revenue: number;
  trend_pct?: number;
  period?: string;
};

export type ReconciliationRecord = {
  id: string;
  entity_type?: string;
  entity_id?: string;
  status: string;
  expected_amount: number;
  actual_amount: number;
  variance: number;
  description?: string;
};

export type LeakageRecord = {
  id: string;
  type: string;
  title: string;
  description?: string;
  amount: number;
  sku?: string;
  status: string;
  severity?: string;
};

export type PayoutForecastRecord = {
  id: string;
  forecast_date: string;
  expected_payout: number;
  reserve_release: number;
  pending_amount: number;
  deferred_revenue: number;
  working_capital: number;
  status: string;
};

export type FinancialHealthRecord = {
  id: string;
  profit_score: number;
  fee_efficiency_score: number;
  settlement_accuracy: number;
  reserve_risk: number;
  cash_flow_score: number;
  marketplace_health: number;
  financial_health_score: number;
  issue?: string;
};

export type FinanceRecommendation = {
  id: string;
  type: string;
  title: string;
  description?: string;
  sku?: string;
  expected_impact?: string;
  confidence_score: number;
  priority_rank: number;
  requires_approval: boolean;
  source_module?: string;
};

const API_BASE = import.meta.env.VITE_FINANCEOPS_API ?? '/api/v1/financeops';

function fetchJSON<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(API_BASE, path, init);
}

export const financeopsApi = {
  dashboard: () => fetchJSON<FinanceOpsDashboard>('/dashboard'),
  listSettlements: () => fetchJSON<SettlementRecord[]>('/settlements'),
  listEvents: () => fetchJSON<FinancialEventRecord[]>('/events'),
  listFees: () => fetchJSON<FeeRecord[]>('/fees'),
  listReconciliation: () => fetchJSON<ReconciliationRecord[]>('/reconciliation'),
  listLeakages: () => fetchJSON<LeakageRecord[]>('/leakages'),
  listForecasts: () => fetchJSON<PayoutForecastRecord[]>('/forecasts'),
  getHealth: () => fetchJSON<FinancialHealthRecord>('/health'),
  listRecommendations: () => fetchJSON<FinanceRecommendation[]>('/recommendations'),
  generateRecommendations: () =>
    fetchJSON<FinanceRecommendation[]>('/recommendations/generate', { method: 'POST' }),
  listSyncJobs: () => fetchJSON<{ id: string; mode: string; status: string }[]>('/sync-jobs'),
  listActivity: () => fetchJSON<{ id: string; message: string }[]>('/activity'),
};
